import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import CardContent from '@mui/material/CardContent';
import Avatar from '@mui/material/Avatar';
import Typography from '@mui/material/Typography';
import { blue } from '@mui/material/colors';
import { CardActionArea, Box, CardActions, Button, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { useState } from 'react';

export default function CardsNews({
  title,
  subheader,
  date,
  image,
  description = '',
  author = '',
  href, // opcional: link externo/interno a la nota
}) {
  const [open, setOpen] = useState(false);
  const initials = author?.trim() ? author.trim().slice(0, 2).toUpperCase() : 'N';

  return (
    <>
      <Card
        role="article"
        aria-labelledby="news-title"
        data-aos="fade-down"
        data-aos-duration="800"
        elevation={2}
        sx={{
          width: '100%',
          height: '100%',             // iguala alturas
          display: 'flex',
          flexDirection: 'column',
          borderRadius: 2,
          transition: 'transform .12s ease, box-shadow .12s ease',
          '&:hover': { transform: 'translateY(-2px)', boxShadow: 6 },
        }}
      >
        <CardActionArea
          component={href ? 'a' : 'div'}
          href={href}
          target={href?.startsWith('http') ? '_blank' : undefined}
          rel={href?.startsWith('http') ? 'noopener' : undefined}
          sx={{ display: 'block' }}
          aria-label={href ? `Abrir: ${title}` : undefined}
        >
          <CardHeader
            avatar={<Avatar sx={{ bgcolor: blue[500] }}>{initials}</Avatar>}
            title={
              <Typography
                id="news-title"
                variant="subtitle1"
                fontWeight={700}
                sx={{
                  display: '-webkit-box',
                  WebkitLineClamp: 2,      // máx. 2 líneas para uniformidad
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                }}
              >
                {title}
              </Typography>
            }
            subheader={date || subheader}
            sx={{ pb: 0.5 }}
          />

          {/* Imagen 16:9 fija */}
          <Box sx={{ position: 'relative', pt: '56.25%' }}>
            <Box
              component="img"
              src={image || '/assets/placeholder.svg'}
              alt={title}
              loading="lazy"
              sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
            />
          </Box>

          {/* Texto recortado para igualar alturas */}
          <CardContent sx={{ flexGrow: 1 }}>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                display: '-webkit-box',
                WebkitLineClamp: 4,        // ajusta a 4–5 según tu diseño
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                minHeight: 80,             // ayuda a uniformar (opcional)
              }}
            >
              {description}
            </Typography>
          </CardContent>
        </CardActionArea>

        {/* Botón fijo abajo; abre Dialog con el texto completo */}
        <CardActions sx={{ mt: 'auto', pt: 0 }}>
          <Button size="small" onClick={() => setOpen(true)} aria-haspopup="dialog">
            Leer más
          </Button>
        </CardActions>
      </Card>

      {/* Dialog con el contenido completo (no altera la altura de la card) */}
      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm" aria-labelledby="news-dialog-title">
        <DialogTitle id="news-dialog-title">{title}</DialogTitle>
        <DialogContent dividers>
          {(date || subheader) && (
            <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
              {date || subheader}
            </Typography>
          )}
          {image && (
            <Box
              component="img"
              src={image}
              alt={title}
              loading="lazy"
              sx={{ width: '100%', height: 'auto', borderRadius: 1, mb: 2 }}
            />
          )}
          <Typography variant="body2" color="text.primary" whiteSpace="pre-line">
            {description}
          </Typography>
        </DialogContent>
        <DialogActions>
          {href && (
            <Button
              component="a"
              href={href}
              target={href.startsWith('http') ? '_blank' : undefined}
              rel={href.startsWith('http') ? 'noopener' : undefined}
            >
              Ir a la nota
            </Button>
          )}
          <Button onClick={() => setOpen(false)} autoFocus>
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
